import { create } from 'zustand';
import { persist } from 'zustand/middleware';

export type CountryCode = 'IQ' | 'SA' | 'AE' | 'KW' | 'QA' | 'BH' | 'OM';

export interface Country {
  code: CountryCode;
  name: string;
  flag: string;
  currency: string;
  currencySymbol: string;
  exchangeRate: number; // How many of this currency = 1 IQD
  priceMarkup: number; // Additional markup percentage (e.g., 1 = 100% = double price)
  shippingCost: number; // In local currency
  freeShippingThreshold: number | null; // null means no free shipping
  cashOnDelivery: boolean;
}

// Exchange rates: 1 IQD = X of target currency (approximate rates)
// Example: 1 IQD ≈ 0.00286 SAR, so 10,000 IQD ≈ 28.6 SAR
export const countries: Country[] = [
  { 
    code: 'IQ', 
    name: 'العراق', 
    flag: '🇮🇶', 
    currency: 'IQD',
    currencySymbol: 'IQD',
    exchangeRate: 1, // Base currency
    priceMarkup: 0, // No markup for Iraq
    shippingCost: 5000,
    freeShippingThreshold: 75000,
    cashOnDelivery: true 
  },
  { 
    code: 'SA', 
    name: 'السعودية', 
    flag: '🇸🇦', 
    currency: 'SAR',
    currencySymbol: 'SAR',
    exchangeRate: 0.00286,
    priceMarkup: 1,
    shippingCost: 43, // ~15,000 IQD
    freeShippingThreshold: null,
    cashOnDelivery: false 
  },
  { 
    code: 'AE', 
    name: 'الإمارات', 
    flag: '🇦🇪', 
    currency: 'AED',
    currencySymbol: 'AED',
    exchangeRate: 0.0028,
    priceMarkup: 1,
    shippingCost: 42, // ~15,000 IQD
    freeShippingThreshold: null,
    cashOnDelivery: false 
  },
  { 
    code: 'KW', 
    name: 'الكويت', 
    flag: '🇰🇼', 
    currency: 'KWD',
    currencySymbol: 'KWD',
    exchangeRate: 0.000237,
    priceMarkup: 1,
    shippingCost: 3.5, // ~15,000 IQD
    freeShippingThreshold: null,
    cashOnDelivery: false 
  },
  { 
    code: 'QA', 
    name: 'قطر', 
    flag: '🇶🇦', 
    currency: 'QAR',
    currencySymbol: 'QAR',
    exchangeRate: 0.00278,
    priceMarkup: 1,
    shippingCost: 42, // ~15,000 IQD
    freeShippingThreshold: null,
    cashOnDelivery: false 
  },
  { 
    code: 'BH', 
    name: 'البحرين', 
    flag: '🇧🇭', 
    currency: 'BHD',
    currencySymbol: 'BHD',
    exchangeRate: 0.00029,
    priceMarkup: 1,
    shippingCost: 4.5, // ~15,000 IQD
    freeShippingThreshold: null,
    cashOnDelivery: false 
  },
  { 
    code: 'OM', 
    name: 'عُمان', 
    flag: '🇴🇲', 
    currency: 'OMR',
    currencySymbol: 'OMR',
    exchangeRate: 0.000294,
    priceMarkup: 1,
    shippingCost: 4.5, // ~15,000 IQD
    freeShippingThreshold: null,
    cashOnDelivery: false 
  },
];

interface CountryStore {
  selectedCountry: CountryCode | null;
  setCountry: (code: CountryCode) => void;
  getCountry: () => Country;
  isGulfCountry: () => boolean;
  getAdjustedPrice: (price: number) => number;
  getShippingCost: (subtotal: number) => number;
  formatPrice: (price: number) => string;
}

export const useCountryStore = create<CountryStore>()(
  persist(
    (set, get) => ({
      selectedCountry: null,
      
      setCountry: (code) => set({ selectedCountry: code }),
      
      getCountry: () => {
        const code = get().selectedCountry || 'IQ';
        return countries.find(c => c.code === code) || countries[0];
      },
      
      isGulfCountry: () => {
        const code = get().selectedCountry;
        return code !== null && code !== 'IQ';
      },
      
      getAdjustedPrice: (priceInIQD) => {
        const country = get().getCountry();
        // Convert from IQD to target currency
        const convertedPrice = priceInIQD * country.exchangeRate;
        // Apply markup
        const finalPrice = convertedPrice * (1 + country.priceMarkup);
        // Round to whole numbers for all currencies
        if (country.code === 'IQ') {
          return Math.round(finalPrice / 1000) * 1000; // Round to nearest 1000 for IQD
        }
        return Math.round(finalPrice); // Round to whole number for other currencies
      },
      
      getShippingCost: (subtotal) => {
        const country = get().getCountry();
        // Check for free shipping threshold
        if (country.freeShippingThreshold !== null && subtotal >= country.freeShippingThreshold) {
          return 0;
        }
        return country.shippingCost;
      },
      
      formatPrice: (price) => {
        const country = get().getCountry();
        return `${price.toLocaleString()} ${country.currencySymbol}`;
      },
    }),
    {
      name: 'country-storage',
    }
  )
);
